import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { Navigate } from "@tanstack/react-router";

import { useCurrentUser } from "@/features/auth";
import { useApiErrorMessage } from "@/shared/api";
import { cn } from "@/shared/lib";
import { BlockError } from "@/shared/ui/block-error";
import { RefreshBar } from "@/shared/ui/refresh";
import { Skeleton } from "@/shared/ui/skeleton";
import { ToggleGroup, ToggleGroupItem } from "@/shared/ui/toggle-group";

import { useTeamDashboard } from "../../api/useTeamDashboard";
import { DASHBOARD_PERIODS, DEFAULT_DASHBOARD_PERIOD } from "../../model/DASHBOARD_PERIODS";
import type { DashboardSearch } from "../../model/dashboardSearchSchema";
import { AnalysesChart } from "./AnalysesChart";
import { Cohorts } from "./Cohorts";
import { Devices } from "./Devices";
import { DrillKinds } from "./DrillKinds";
import { Findings } from "./Findings";
import { Funnel } from "./Funnel";
import { KpiTiles } from "./KpiTiles";
import { NewPlayers } from "./NewPlayers";
import { PlayersChart } from "./PlayersChart";
import { Sections } from "./Sections";
import { Sources } from "./Sources";
import { WhoPlayers } from "./WhoPlayers";

// Сколько главных цифр — столько заготовок при загрузке.
const KPI_TILES = 8;

const DAYS = msg({ message: "{days, plural, one {# day} other {# days}}" });

type DashboardPageProps = {
  search: DashboardSearch;
  onSearchChange: (search: DashboardSearch) => void;
};

// «Дашборд» команды по макету design/prototype/DashboardIngest.dc.html («14б»): главные цифры
// периода, игроки по дням, путь нового игрока, источники привлечения, возврат по дням прихода,
// устройства, разборы, разделы и действия, тренировки по типам, находки, кто наши игроки и
// новые игроки (`GET /api/dashboard`). Только для `is_staff`: остальных уводит на главную. Период — в search
// params; при смене прошлые цифры видны приглушённо, пока не придут новые.
export const DashboardPage = ({ search, onSearchChange }: DashboardPageProps) => {
  const { i18n, t } = useLingui();
  const { data: user } = useCurrentUser();
  const staff = user?.is_staff === true;
  const period = search.period ?? DEFAULT_DASHBOARD_PERIOD;
  const dashboard = useTeamDashboard(period, staff);
  const errorMessage = useApiErrorMessage(dashboard.error);

  if (user !== undefined && !staff) {
    return <Navigate to="/" replace />;
  }

  const data = dashboard.data;
  const updated = new Intl.DateTimeFormat(i18n.locale, { hour: "2-digit", minute: "2-digit" });
  const fetchedAt = data?.umami_fetched_at;
  const umamiTime = fetchedAt == null ? null : updated.format(new Date(fetchedAt));

  const body = () => {
    if (data === undefined) {
      if (dashboard.isError) {
        return (
          <BlockError
            title={<Trans>Could not load the dashboard</Trans>}
            message={errorMessage}
            retrying={dashboard.isRefetching}
            onRetry={() => void dashboard.refetch()}
          />
        );
      }
      return (
        <div data-state="loading" className="flex flex-col gap-5">
          <div className="@container">
            <div className="grid grid-cols-2 gap-3 @min-[46rem]:grid-cols-4">
              {Array.from({ length: KPI_TILES }, (_, index) => (
                <Skeleton key={index} className="h-[116px] rounded-xl" />
              ))}
            </div>
          </div>
          <Skeleton className="h-80 rounded-[14px]" />
          <div className="grid grid-cols-[repeat(auto-fit,minmax(min(380px,100%),1fr))] gap-5">
            <Skeleton className="h-80 rounded-[14px]" />
            <Skeleton className="h-80 rounded-[14px]" />
          </div>
        </div>
      );
    }
    return (
      <div
        aria-busy={dashboard.isPlaceholderData}
        className={cn(
          "relative flex flex-col gap-5 transition-opacity",
          dashboard.isPlaceholderData && "opacity-60",
        )}
      >
        <RefreshBar active={dashboard.isFetching} />
        <KpiTiles totals={data.totals} funnel={data.funnel} />
        <PlayersChart days={data.days} />
        <div className="grid grid-cols-[repeat(auto-fit,minmax(min(380px,100%),1fr))] gap-5">
          <Funnel funnel={data.funnel} />
          <Sources
            sources={data.sources}
            topInviter={data.top_inviter}
            inAppPlayers={data.in_app_players}
          />
          <AnalysesChart
            days={data.days}
            failures={data.failures}
            medianSeconds={data.analysis_median_seconds}
            freshness={data.freshness}
          />
          <Sections sections={data.sections} events={data.events} />
          <Cohorts cohorts={data.cohorts} />
          <Devices devices={data.devices} />
          <DrillKinds
            kinds={data.drill_kinds}
            answers={data.drill_answers}
            seriesStarted={data.drill_series_started}
            seriesFinished={data.drill_series_finished}
          />
          <Findings findings={data.findings} analyzedPlayers={data.analyzed_players} />
        </div>
        <WhoPlayers ranks={data.ranks} heroes={data.heroes} locales={data.locales} />
        <NewPlayers
          players={data.new_players}
          total={data.totals.new_players}
          today={new Date(data.end).toISOString().slice(0, 10)}
        />
      </div>
    );
  };

  return (
    <main
      data-page="dashboard"
      className="mx-auto flex w-full max-w-[1200px] flex-col gap-5 px-6 pt-6 pb-16"
    >
      <header className="flex flex-col gap-1.5">
        <span className="self-start rounded-full border border-border-strong px-2.5 py-0.5 text-xs font-semibold text-accent">
          <Trans>Team only</Trans>
        </span>
        <h1 className="font-display text-[34px] leading-tight">
          <Trans>Dashboard</Trans>
        </h1>
        <p className="text-[13px] text-subtle">
          {umamiTime === null ? (
            <Trans>
              Umami is not connected yet, so there are no visitors; everything else comes from the
              Relock database.
            </Trans>
          ) : (
            <Trans>
              Visitors, sources and sections come from Umami (updated at {umamiTime}); everything
              else comes from the Relock database.
            </Trans>
          )}
        </p>
      </header>
      <ToggleGroup
        type="single"
        aria-label={t`Period`}
        className="self-start"
        value={String(period)}
        onValueChange={(value) => {
          const next = DASHBOARD_PERIODS.find((days) => String(days) === value);
          if (next !== undefined) {
            onSearchChange({ ...search, period: next });
          }
        }}
      >
        {DASHBOARD_PERIODS.map((days) => (
          <ToggleGroupItem key={days} value={String(days)}>
            {i18n._({ ...DAYS, values: { days } })}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>
      {body()}
    </main>
  );
};
